"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Briefcase, Users, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const advisors = [{
  id: "adv-1",
  name: "Andrei Vargas",
  role: "Founder & Humanitarian Lead",
  specialty: "Philanthropic Travel",
  image: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/andrei-vargas-pic.jpg?v=g9t1ebrior",
  bio: "Andrei has dedicated his life to humanitarian discovery, mentored by parents who taught him to 'always leave it better than you found it.' With 35 years of global experience, he believes travel is a bridge to saving the world and its people.",
  caseStudies: ["Orchestrated the delivery of medical supplies to local communities in rural Cuba.", "Developed sustainable tourism models for indigenous territories in the Amazon."],
  placesVisited: "Medellin, Cartagena, Havana, Amazon Rainforest, Cusco, San Juan",
  nameClassName: "justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:text-accent-foreground dark:hover:bg-accent/50 has-[>svg]:px-3 group/avatar group/avatar !bg-[url(https://certain-robin.10web.cloud/wp-content/uploads/2026/04/founder_mark_valencia.png)] h-auto w-full flex flex-col items-center p-4 hover:bg-transparent space-y-4 group/avatar !bg-[url(https://certain-robin.10web.cloud/wp-content/uploads/2026/04/advisor_sofia_ramirez.png)]"
}, {
  id: "adv-2",
  name: "Mark Valencia, Ph.D.",
  role: "Founder & Academic Director",
  specialty: "Cultural Anthropology",
  image: "https://certain-robin.10web.cloud/wp-content/uploads/2026/05/IMG_1331-Copy-2.jpg?v=26putykbdvu",
  bio: "Dr. Valencia combines academic rigor with a profound love for local cultures. He co-founded PS Travel to 'do well and do good,' ensuring every curated experience is as educational as it is luxurious and deeply connected.",
  caseStudies: ["Led a cross-disciplinary research project on heritage preservation in Naxos, Greece.", "Designed an educational summer exchange program focusing on Mediterranean history."],
  placesVisited: "Naxos, Athens, Rome, Cairo, Kyoto, Jerusalem, Mexico City",
  nameClassName: ""
}];
export default function AdvisoryCollective() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="46345" id="meet-the-visionaries" ref={ref} className="dark bg-background py-24 sm:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent opacity-50" />
      
      <div className="container mx-auto px-6 lg:px-8">
        <div className={`max-w-2xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl font-bold tracking-tight text-secondary sm:text-5xl font-default">
            Meet the Visionaries
          </h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground font-sans">Rooted in a lifetime of global exploration, our founders lead with a simple philosophy: Not just destinations — experiences. We believe in collective discovery and leaving every place better than we found it.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 lg:gap-y-16 justify-items-center">
          {advisors.map((advisor, i) => <div key={advisor.id} data-index={i} className={`group flex flex-col items-center text-center transition-all duration-700 delay-[${i * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" className={`${advisor.nameClassName || ""} h-auto w-full flex flex-col items-center p-4 hover:bg-transparent space-y-4 group/avatar`}>
                    <div className="relative mx-auto mb-2">
                      <div className="absolute inset-0 rounded-lg border border-secondary/20 scale-105 opacity-0 transition-all duration-500 group-hover/avatar:scale-110 group-hover/avatar:opacity-100" />
                    </div>
                    
                    <div className="space-y-2 flex flex-col items-center">
                      <h3 className="text-2xl font-bold leading-7 tracking-tight text-primary font-default group-hover/avatar:text-secondary transition-colors italic">
                        {advisor.name}
                      </h3>
                      <p className="text-sm font-medium text-muted-foreground border-t border-secondary/30 pt-2 w-full">
                        {advisor.role}
                      </p>
                      <Badge variant="default" className="mt-2 bg-primary hover:bg-primary/90 text-primary-foreground border-none shadow-md font-mono tracking-wider text-[10px] uppercase">
                        {advisor.specialty}
                      </Badge>
                    </div>
                  </Button>
                </DialogTrigger>
                
                <DialogContent className="sm:max-w-lg md:max-w-2xl bg-card border-secondary/20">
                  <DialogHeader>
                    <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-4">
                      <SafeHydrate><Avatar className="size-24 sm:size-32 border-2 border-secondary/30 shadow-xl rounded-sm grayscale hover:grayscale-0 transition-all">
                        <AvatarImage src={advisor.image} alt={advisor.name} className="object-contain object-center" />
                        <AvatarFallback>{advisor.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div className="text-left space-y-1">
                        <DialogTitle className="text-3xl sm:text-4xl font-bold text-secondary font-default italic">
                          {advisor.name}
                        </DialogTitle>
                        <p className="text-primary font-medium text-lg font-sans">{advisor.role}</p>
                        <Badge variant="outline" className="mt-1 border-secondary/30 text-secondary bg-secondary/5 font-mono uppercase text-[10px]">
                          {advisor.specialty}
                        </Badge>
                      </div>
                    </div>
                  </DialogHeader>
                  
                  <div className="space-y-6 py-4">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-widest text-secondary mb-2 flex items-center gap-2 font-mono">
                        <Users className="size-4 text-primary" /> Our Collective Discovery
                      </h4>
                      <DialogDescription className="text-base text-foreground/90 leading-relaxed font-sans">
                        {advisor.bio}
                      </DialogDescription>
                    </div>

                    <div className="bg-muted/30 p-4 rounded-sm border border-secondary/10">
                      <h4 className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3 flex items-center gap-2 font-mono">
                        <MapPin className="size-4 text-primary" /> Places Visited
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {advisor.placesVisited.split(", ").map((place, idx) => <Badge key={idx} variant="outline" className="border-secondary/20 text-muted-foreground font-sans text-[11px]">
                            {place}
                          </Badge>)}
                      </div>
                    </div>

                    <div className="bg-muted/30 p-4 rounded-sm border border-secondary/10">
                      <h4 className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3 flex items-center gap-2 font-mono">
                        <Briefcase className="size-4 text-primary" /> Global Impact & Expertise
                      </h4>
                      <ul className="space-y-3">
                        {advisor.caseStudies.map((study, idx) => <li key={idx} className="flex gap-3 text-sm text-foreground/80 items-start font-sans">
                            <span className="mt-1.5 size-1.5 rounded-lg bg-secondary shrink-0" />
                            <span>{study}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>

                  <DialogFooter className="sm:justify-between gap-4 items-center border-t border-secondary/20 pt-6 mt-2">
                    <DialogClose asChild>
                      
                    </DialogClose>
                    <Button asChild className="w-full sm:w-auto shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest uppercase">
                      
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}